import {
  createRouter,
  createWebHistory,
  type Router,
  type RouteRecordRaw,
} from 'vue-router';
import type { ProjectRole } from '@cosmic-safari/shared';
import { authGuard } from './guards.js';

declare module 'vue-router' {
  interface RouteMeta {
    /** Public routes bypass the authenticated-user redirect (see guards.ts). */
    public?: boolean;
    /** Staff-only routes: non-staff callers are rewritten to `not-found`. */
    staff?: boolean;
    /**
     * Minimum project role required. An authenticated caller below this role on
     * `:projectId` is sent to the per-project 403 page (see guards.ts). `viewer`
     * means "any member"; per-tab admin gating is component-level.
     */
    requiredRole?: ProjectRole;
    /**
     * Full-viewport routes render without the app shell (no header, no centered
     * column) — the canvas comparison page owns the whole viewport and supplies
     * its own exit (see App.vue).
     */
    fullViewport?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/projects' },
  {
    path: '/login',
    component: () => import('@/pages/Login.vue'),
    meta: { public: true },
  },
  {
    path: '/logout',
    component: () => import('@/pages/Logout.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    component: () => import('@/pages/ForgotPassword.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    component: () => import('@/pages/ResetPassword.vue'),
    meta: { public: true },
  },
  {
    path: '/invitations/accept',
    component: () => import('@/pages/InvitationAccept.vue'),
    meta: { public: true },
  },
  {
    path: '/set-password/:token',
    component: () => import('@/pages/SetPassword.vue'),
    meta: { public: true },
  },
  { path: '/projects', component: () => import('@/pages/Projects.vue') },
  {
    // Registered BEFORE the dynamic `:projectId` route so the static `new`
    // segment is not captured as a project id. Org-role gating (owner/admin) is
    // component/affordance-level, not a route meta — a member who deep-links
    // here simply gets a 403 from the endpoint on submit.
    path: '/projects/new',
    component: () => import('@/pages/CreateProject.vue'),
  },
  {
    // The optional `:tab?` selects the project surface (builds | baselines); a
    // bare /projects/:projectId defaults to the Builds tab inside ProjectDetail
    // (mirrors the settings route below). `viewer` = any member: a non-member is
    // 403'd at the route, same as settings. The static `settings`/`forbidden`
    // segments below outrank the dynamic `:tab?`, so those paths keep matching
    // their own routes.
    path: '/projects/:projectId/:tab?',
    component: () => import('@/pages/ProjectDetail.vue'),
    meta: { requiredRole: 'viewer' },
  },
  {
    path: '/projects/:projectId/forbidden',
    name: 'forbidden',
    component: () => import('@/pages/Forbidden.vue'),
  },
  {
    path: '/projects/:projectId/settings/:tab?',
    component: () => import('@/pages/ProjectSettings.vue'),
    // `viewer` = any member: a non-member is 403'd at the route; the Config /
    // Members / Tokens tabs gate to admin/owner inside ProjectSettings.vue.
    meta: { requiredRole: 'viewer' },
  },
  {
    // The optional `:tab?` selects the build surface (comparisons | details |
    // environments); a bare /builds/:buildId defaults to the Comparisons tab
    // inside BuildDetail (mirrors the /projects/:projectId/:tab? route above).
    // The deeper two-segment `comparisons/:comparisonId` route below is not
    // shadowed by the single dynamic `:tab?` — the longer/static path outranks
    // it, the same ranking ProjectDetailTabs relies on.
    path: '/builds/:buildId/:tab?',
    component: () => import('@/pages/BuildDetail.vue'),
  },
  {
    path: '/builds/:buildId/comparisons/:comparisonId',
    component: () => import('@/pages/ComparisonDetail.vue'),
    meta: { fullViewport: true },
  },
  {
    // The journey map (HUBL-074), the comparison canvas's sibling deep route:
    // full-viewport, its own exit back to the build's Journeys tab. Two segments
    // with a static `journeys` outrank the single dynamic `:tab?` above, so
    // /builds/:id/journeys still resolves to the tab — the ranking the
    // comparison route already relies on, pinned by a router test.
    // `:journeyId` is a catalogue slug (never a uuid).
    path: '/builds/:buildId/journeys/:journeyId',
    component: () => import('@/pages/JourneyMap.vue'),
    meta: { fullViewport: true },
  },
  { path: '/admin', redirect: '/admin/orgs' },
  {
    path: '/admin/orgs',
    component: () => import('@/pages/AdminOrgs.vue'),
    meta: { staff: true },
  },
  {
    path: '/admin/orgs/new',
    component: () => import('@/pages/AdminCreateOrg.vue'),
    meta: { staff: true },
  },
  {
    path: '/admin/orgs/:orgId',
    component: () => import('@/pages/AdminOrgDetail.vue'),
    meta: { staff: true },
  },
  {
    path: '/admin/users',
    component: () => import('@/pages/AdminUsers.vue'),
    meta: { staff: true },
  },
  {
    path: '/admin/users/:userId',
    component: () => import('@/pages/AdminUserDetail.vue'),
    meta: { staff: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFound.vue'),
  },
];

export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(),
    routes,
  });
  router.beforeEach(authGuard);
  return router;
}
